import { useCallback, useContext } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Contribution,
  ContributionCollection,
} from "wealthmode-frontend/backend"
import { Box, HStack, Text, VStack } from "wealthmode-frontend/gluestack/ui"
import { formatMoneyFull } from "wealthmode-frontend/util"

import { DataTable, IDLink } from "@/components"
import { BackendContext } from "@/context"
import { contributionAmount, contributionTotal } from "@/lib/contributions"
import { CONTRIBUTION_KIND_LABELS, contributionLimitKindLabel } from "@/lib/labels"

export default function Contributions() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const contributions = data.link("contributions")?.resolve() as ContributionCollection | undefined
    if (!contributions) {
      return undefined
    }

    await contributions.fetch({ reload: true })

    return contributions
  }, [backend, client_id, account_id])

  return (
    <Box>
      <HStack>
        <Box className="flex-1">
          <DataTable
            title={"Contributions"}
            load={load}
            header={[
              { id: "id", title: "ID", shrink: true },
              { id: "tax_year", title: "Tax Year", shrink: true },
              { id: "covered", title: "Covered" },
              ...CONTRIBUTION_KIND_LABELS.map(({ kind, title }) => ({ id: kind, title })),
              { id: "total", title: "Total" },
              { id: "limits", title: "Limits" },
            ]}
            render={(model: Contribution, field: string) => {
              switch (field) {
                case "id":
                  return (
                    <IDLink
                      href={`/clients/${client_id}/accounts/${account_id}/contributions/${model.id}`}
                      id={model.id}
                    />
                  )

                case "covered":
                  return coverage(model)

                case "total":
                  return formatMoneyFull(contributionTotal(model.sources()))

                case "limits": {
                  const limits = model.limits()
                  if (limits.length === 0) {
                    return "--"
                  }

                  return (
                    <VStack>
                      {limits.map((limit) => (
                        <Link
                          key={limit.id}
                          href={`/contribution_limits/${limit.id}`}
                        >
                          <Text className="text-markdown-link hover:opacity-80 underline whitespace-nowrap">
                            {contributionLimitKindLabel(limit.attributes.kind)}:{" "}
                            {formatMoneyFull(model.countedToward(limit))} / {formatMoneyFull(limit.attributes.maximum)}
                          </Text>
                        </Link>
                      ))}
                    </VStack>
                  )
                }
              }

              if (CONTRIBUTION_KIND_LABELS.some(({ kind }) => kind === field)) {
                const amount = contributionAmount(model.sources(), field)
                return amount == null ? "--" : formatMoneyFull(amount)
              }

              return model.get(field)
            }}
          />
        </Box>
      </HStack>
    </Box>
  )
}

/**
 * The stretch of the year the account's history covers. A year with none holds
 * contributions only because a deposit made the following spring was made for
 * it.
 */
function coverage(model: Contribution): string {
  const { covered_from, covered_through } = model.attributes
  if (covered_from == null || covered_through == null) {
    return "--"
  }

  return `${covered_from} – ${covered_through}`
}
